<template>
  <Box :close-on-click-outside="true" :handleClose="closeDialog">
    <div class="dialog" @keydown.esc="() => closeDialog(null)">
      <div class="header has-bcc-background has-text-white">
        <span>{{ questionnaireData.accountNumber }} - {{ questionnaireData.accountName }}</span>
      </div>

      <div class="body">
        <!-- Tab Navigation -->
        <div class="tabs">
          <ul>
            <li :class="{ 'is-active': activeTab === 'bank' }">
              <a @click="activeTab = 'bank'">
                <span class="icon is-small"><i class="fas fa-building" aria-hidden="true"></i></span>
                <span>Bank Contact Information</span>
              </a>
            </li>
            <li :class="{ 'is-active': activeTab === 'participants' }">
              <a @click="activeTab = 'participants'">
                Participants
                <span class="tag is-rounded has-bcc-background has-text-white" style="margin-left: 8px">
                  {{ questionnaireParticipants.length }}
                </span>
              </a>
            </li>
            <li :class="{ 'is-active': activeTab === 'contacts' }">
              <a @click="activeTab = 'contacts'">
                Contacts
                <span class="tag is-rounded has-bcc-background has-text-white" style="margin-left: 8px">
                  {{ questionnaireContacts.length }}
                </span>
              </a>
            </li>
          </ul>
        </div>

        <!-- Tab Content -->
        <div class="participants-grid-container">
          <!-- Bank Contact Information Tab -->
          <div v-if="activeTab === 'bank'">
            <div class="bank-info-container">
              <div class="columns">
                <!-- Legal Address -->
                <div class="column is-4">
                  <div class="info-section">
                    <h5 class="has-text-weight-bold section-title">Legal Address</h5>
                    <div class="info-field" :title="originalBankContactInfo.primaryAddress?.street">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label has-text-weight-bold">Address</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.primaryAddress?.street !== originalBankContactInfo?.primaryAddress?.street && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.primaryAddress?.street || '-' : originalBankContactInfo?.primaryAddress?.street || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.primaryAddress?.street || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="info-field" :title="originalBankContactInfo.primaryAddress?.city">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label has-text-weight-bold">City</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.primaryAddress?.city !== originalBankContactInfo?.primaryAddress?.city && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.primaryAddress?.city || '-' : originalBankContactInfo?.primaryAddress?.city || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.primaryAddress?.city || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="columns is-gapless">
                      <div class="column is-6">
                        <div class="info-field" :title="originalBankContactInfo.primaryAddress?.state">
                          <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                            <label class="label has-text-weight-bold">State</label>
                            <div class="field-value" :class="{ 'has-text-success': bankContactInfo.primaryAddress?.state !== originalBankContactInfo?.primaryAddress?.state && !showOriginalValues }">
                              {{ !showOriginalValues ? bankContactInfo.primaryAddress?.state || '-' : originalBankContactInfo?.primaryAddress?.state || '-' }}
                            </div>

                            <template #content>
                              {{ originalBankContactInfo?.primaryAddress?.state || '-' }}
                            </template>
                          </Popper>
                        </div>
                      </div>
                      <div class="column is-6" style="padding-left: 0.5rem">
                        <div class="info-field" :title="originalBankContactInfo.primaryAddress?.postalCode">
                          <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                            <label class="label has-text-weight-bold">Zip Code</label>
                            <div class="field-value" :class="{ 'has-text-success': bankContactInfo.primaryAddress?.postalCode !== originalBankContactInfo?.primaryAddress?.postalCode && !showOriginalValues }">
                              {{ !showOriginalValues ? bankContactInfo.primaryAddress?.postalCode || '-' : originalBankContactInfo?.primaryAddress?.postalCode || '-' }}
                            </div>

                            <template #content>
                              {{ originalBankContactInfo?.primaryAddress?.postalCode || '-' }}
                            </template>
                          </Popper>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Mailing Address -->
                <div class="column is-4">
                  <div class="info-section">
                    <h5 class="has-text-weight-bold section-title">Mailing Address</h5>
                    <div class="info-field" v-if="showOriginalValues ? originalBankContactInfo.mailingAddressIsSame : bankContactInfo.mailingAddressIsSame" :title="originalBankContactInfo.mailingAddressIsSame ? 'Same as Legal' : ''">
                      <div class="field-value">
                        <span class="tag is-info is-small">Same as Legal</span>
                      </div>
                    </div>
                    <div class="info-field" :title="originalBankContactInfo.mailingAddress?.street">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label has-text-weight-bold">Address</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.mailingAddress?.street !== originalBankContactInfo?.mailingAddress?.street && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.mailingAddress?.street || '-' : originalBankContactInfo?.mailingAddress?.street || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.mailingAddress?.street || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="info-field" :title="originalBankContactInfo.mailingAddress?.city">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label has-text-weight-bold">City</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.mailingAddress?.city !== originalBankContactInfo?.mailingAddress?.city && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.mailingAddress?.city || '-' : originalBankContactInfo?.mailingAddress?.city || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.mailingAddress?.city || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="columns is-gapless">
                      <div class="column is-6">
                        <div class="info-field" :title="originalBankContactInfo.mailingAddress?.state">
                          <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                            <label class="label has-text-weight-bold">State</label>
                            <div class="field-value" :class="{ 'has-text-success': bankContactInfo.mailingAddress?.state !== originalBankContactInfo?.mailingAddress?.state && !showOriginalValues }">
                              {{ !showOriginalValues ? bankContactInfo.mailingAddress?.state || '-' : originalBankContactInfo?.mailingAddress?.state || '-' }}
                            </div>

                            <template #content>
                              {{ originalBankContactInfo.mailingAddress?.state || '-' }}
                            </template>
                          </Popper>
                        </div>
                      </div>
                      <div class="column is-6" style="padding-left: 0.5rem">
                        <div class="info-field" :title="originalBankContactInfo.mailingAddress?.postalCode">
                          <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                            <label class="label has-text-weight-bold">Zip Code</label>
                            <div class="field-value" :class="{ 'has-text-success': bankContactInfo.mailingAddress?.postalCode !== originalBankContactInfo?.mailingAddress?.postalCode && !showOriginalValues }">
                              {{ !showOriginalValues ? bankContactInfo.mailingAddress?.postalCode || '-' : originalBankContactInfo?.mailingAddress?.postalCode || '-' }}
                            </div>

                            <template #content>
                              {{ originalBankContactInfo.mailingAddress?.postalCode || '-' }}
                            </template>
                          </Popper>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Contact Information -->
                <div class="column is-4">
                  <div class="info-section">
                    <h5 class="has-text-weight-bold section-title">Contact Information</h5>
                    <div class="info-field" :title="originalBankContactInfo.clientPhone">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label">Main Phone</label>
                        <div class="field-value has-text-info">
                          <span v-if="bankContactInfo.clientPhone">
                            <a class="has-text-info" :class="{ 'has-text-success': bankContactInfo.clientPhone !== originalBankContactInfo.clientPhone && !showOriginalValues }" :href="`tel:${bankContactInfo.clientPhone}`">{{
                              !showOriginalValues ? bankContactInfo.clientPhone : originalBankContactInfo.clientPhone
                            }}</a>
                          </span>
                          <span v-else>-</span>
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.clientPhone || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="info-field" :title="originalBankContactInfo.clientFax">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label">Main Fax</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.clientFax !== originalBankContactInfo.clientFax && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.clientFax || '-' : originalBankContactInfo?.clientFax || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.clientFax || '-' }}
                        </template>
                      </Popper>
                    </div>
                    <div class="info-field" :title="originalBankContactInfo.clientTIN">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <label class="label">TIN</label>
                        <div class="field-value" :class="{ 'has-text-success': bankContactInfo.clientTIN !== originalBankContactInfo.clientTIN && !showOriginalValues }">
                          {{ !showOriginalValues ? bankContactInfo.clientTIN || '-' : originalBankContactInfo?.clientTIN || '-' }}
                        </div>

                        <template #content>
                          {{ originalBankContactInfo?.clientTIN || '-' }}
                        </template>
                      </Popper>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Participants Tab -->
          <div v-if="activeTab === 'participants'">
            <div v-if="questionnaireParticipants.length === 0" class="empty-state">
              <p>No participants found for this questionnaire.</p>
            </div>

            <div v-else class="table-container">
              <table class="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Name Changed</th>
                    <th>Title</th>
                    <th>Employee ID</th>
                    <th>Branch/Department</th>
                    <th>Hire Date</th>
                    <th>Termination Date</th>
                    <th>Termination Reason</th>
                    <th>Base Salary</th>
                    <th v-if="showCompensationColumns">Bonus</th>
                    <th v-if="showCompensationColumns">Other Compensation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(participant, index) in questionnaireParticipants" :key="participant.participantId">
                    <td class="participant-name">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': participant.firstName !== originalQuestionnaireParticipants[index]?.firstName && !showOriginalValues }">
                          {{ !showOriginalValues ? participant.firstName : originalQuestionnaireParticipants[index]?.firstName }}
                        </div>

                        <template #content>
                          {{ originalQuestionnaireParticipants[index]?.firstName || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="participant-name">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': participant.lastName !== originalQuestionnaireParticipants[index]?.lastName && !showOriginalValues }">
                          {{ !showOriginalValues ? participant.lastName : originalQuestionnaireParticipants[index]?.lastName }}
                        </div>

                        <template #content>
                          {{ originalQuestionnaireParticipants[index]?.lastName || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="participant-name-changed">
                      <div style="display: flex; justify-content: center; align-items: center; height: 100%">
                        <input type="checkbox" :checked="participant.isNameChanged" disabled />
                      </div>
                    </td>
                    <td class="participant-title">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': participant.title !== originalQuestionnaireParticipants[index]?.title && !showOriginalValues }">
                          {{ !showOriginalValues ? participant.title : originalQuestionnaireParticipants[index]?.title }}
                        </div>
                        <template #content>
                          {{ originalQuestionnaireParticipants[index]?.title || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="participant-employee-id">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': participant.bankEmployeeNo !== originalQuestionnaireParticipants[index]?.bankEmployeeNo && !showOriginalValues }">
                          {{ !showOriginalValues ? participant.bankEmployeeNo : originalQuestionnaireParticipants[index]?.bankEmployeeNo }}
                        </div>
                        <template #content>
                          {{ originalQuestionnaireParticipants[index]?.bankEmployeeNo || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="participant-branch-department">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': participant.branchDepartment !== originalQuestionnaireParticipants[index]?.branchDepartment && !showOriginalValues }">
                          {{ !showOriginalValues ? participant.branchDepartment : originalQuestionnaireParticipants[index]?.branchDepartment }}
                        </div>
                        <template #content>
                          {{ originalQuestionnaireParticipants[index]?.branchDepartment || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="hire-date">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <span :class="{ 'has-text-success': participant.hireDate !== originalQuestionnaireParticipants[index]?.hireDate && !showOriginalValues }">{{
                          !showOriginalValues ? formatDate(participant.hireDate) : formatDate(originalQuestionnaireParticipants[index]?.hireDate)
                        }}</span>

                        <template #content>
                          {{ formatDate(originalQuestionnaireParticipants[index]?.hireDate) || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="termination-date">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <span :class="{ 'has-text-success': participant.terminationDate !== originalQuestionnaireParticipants[index]?.terminationDate && !showOriginalValues }">{{
                          !showOriginalValues ? formatDate(participant.terminationDate) : formatDate(originalQuestionnaireParticipants[index]?.terminationDate)
                        }}</span>

                        <template #content>
                          {{ formatDate(originalQuestionnaireParticipants[index]?.terminationDate) || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="termination-reason">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <span :class="{ 'has-text-success': participant.terminationReason !== originalQuestionnaireParticipants[index]?.terminationReason && !showOriginalValues }">{{
                          !showOriginalValues ? handleReasonText(participant.terminationReason) : handleReasonText(originalQuestionnaireParticipants[index]?.terminationReason)
                        }}</span>

                        <template #content>
                          {{ handleReasonText(originalQuestionnaireParticipants[index]?.terminationReason) || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="salary" :class="{ unavailable: handleAvailability(participant) }">
                      <span class="has-text-weight-semibold">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <span :class="{ 'has-text-success': participant.salary !== originalQuestionnaireParticipants[index]?.salary && !showOriginalValues }">{{
                            !showOriginalValues ? formatCurrency(participant.salary) : formatCurrency(originalQuestionnaireParticipants[index]?.salary)
                          }}</span>
                          <template #content>
                            {{ formatCurrency(originalQuestionnaireParticipants[index]?.salary) || '-' }}
                          </template>
                        </Popper>
                      </span>
                    </td>
                    <td class="bonus" v-if="showCompensationColumns" :class="{ unavailable: handleAvailability(participant) }">
                      <span class="has-text-weight-semibold">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <span :class="{ 'has-text-success': participant.bonus !== originalQuestionnaireParticipants[index]?.bonus && !showOriginalValues }">{{
                            !showOriginalValues ? formatCurrency(participant.bonus) : formatCurrency(originalQuestionnaireParticipants[index]?.bonus)
                          }}</span>
                          <template #content>
                            {{ formatCurrency(originalQuestionnaireParticipants[index]?.bonus) || '-' }}
                          </template>
                        </Popper>
                      </span>
                    </td>
                    <td class="other-compensation" v-if="showCompensationColumns" :class="{ unavailable: handleAvailability(participant) }">
                      <span class="has-text-weight-semibold">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <span :class="{ 'has-text-success': participant.otherCompensation !== originalQuestionnaireParticipants[index]?.otherCompensation && !showOriginalValues }">{{
                            !showOriginalValues ? formatCurrency(participant.otherCompensation) : formatCurrency(originalQuestionnaireParticipants[index]?.otherCompensation)
                          }}</span>
                          <template #content>
                            {{ formatCurrency(originalQuestionnaireParticipants[index]?.otherCompensation) || '-' }}
                          </template>
                        </Popper>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div v-if="activeTab === 'contacts'">
            <div v-if="questionnaireContacts.length === 0" class="empty-state">
              <p>No contacts found for this questionnaire.</p>
            </div>

            <div v-else class="table-container">
              <table class="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th>Primary Contact</th>
                    <th>Name</th>
                    <th>Title</th>
                    <th>Phone</th>
                    <th>Extension</th>
                    <th>Email</th>
                    <th>No Longer at Bank</th>
                    <th>Remove Access</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(contact, index) in questionnaireContacts" :key="contact.id">
                    <td class="contact-primary-contact">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': contact.isPrimaryContact !== originalQuestionnaireContacts[index]?.isPrimaryContact && !showOriginalValues }">
                          {{ !showOriginalValues ? (contact.isPrimaryContact ? 'Yes' : 'No') : originalQuestionnaireContacts[index]?.isPrimaryContact ? 'Yes' : 'No' }}
                        </div>

                        <template #content>
                          {{ originalQuestionnaireContacts[index]?.isPrimaryContact ? 'Yes' : 'No' || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="contact-name">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': `${contact.firstName} ${contact.lastName}` !== originalQuestionnaireContacts[index]?.originalFullName && !showOriginalValues }">
                          {{ !showOriginalValues ? `${contact.firstName} ${contact.lastName}` : originalQuestionnaireContacts[index]?.originalFullName }}
                        </div>

                        <template #content>
                          {{ originalQuestionnaireContacts[index]?.originalFullName || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="contact-title">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <div class="has-text-weight-bold" :class="{ 'has-text-success': contact.title !== originalQuestionnaireContacts[index]?.title && !showOriginalValues }">
                          {{ !showOriginalValues ? contact.title : originalQuestionnaireContacts[index]?.title }}
                        </div>

                        <template #content>
                          {{ originalQuestionnaireContacts[index]?.title || '-' }}
                        </template>
                      </Popper>
                    </td>
                    <td class="contact-phone">
                      <span v-if="contact.phone">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <a :href="`tel:${contact.phone}`" class="has-text-info" :class="{ 'has-text-success': contact.phone !== originalQuestionnaireContacts[index]?.phone && !showOriginalValues }">{{
                            !showOriginalValues ? contact.phone : originalQuestionnaireContacts[index]?.phone
                          }}</a>

                          <template #content>
                            {{ originalQuestionnaireContacts[index]?.phone || '-' }}
                          </template>
                        </Popper>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="contact-extension">
                      <span v-if="contact.extension">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <span :class="{ 'has-text-success': contact.extension !== originalQuestionnaireContacts[index]?.extension && !showOriginalValues }">{{
                            !showOriginalValues ? contact.extension : originalQuestionnaireContacts[index]?.extension
                          }}</span>

                          <template #content>
                            {{ originalQuestionnaireContacts[index]?.extension || '-' }}
                          </template>
                        </Popper>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="contact-extension">
                      <span v-if="contact.email">
                        <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                          <a :href="`mailto:${contact.email}`" class="has-text-info" :class="{ 'has-text-success': contact.email !== originalQuestionnaireContacts[index]?.email && !showOriginalValues }">{{
                            !showOriginalValues ? contact.email : originalQuestionnaireContacts[index]?.email
                          }}</a>

                          <template #content>
                            {{ originalQuestionnaireContacts[index]?.email || '-' }}
                          </template>
                        </Popper>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="contact-status">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <!-- <span v-if="showOriginalValues ? originalQuestionnaireContacts[index]?.isDeleted : contact.isDeleted" class="tag is-danger">Removed</span>
                        <span v-else class="tag is-success">Active</span> -->
                        <input type="checkbox" :checked="showOriginalValues ? originalQuestionnaireContacts[index]?.noLongerAtBank : contact.noLongerAtBank" disabled />
                        <template #content>
                          <input type="checkbox" :checked="originalQuestionnaireContacts[index]?.noLongerAtBank" disabled />
                        </template>
                      </Popper>
                    </td>
                    <td class="contact-status">
                      <Popper :hover="!showOriginalValues && questionnaireData.status >= 2" placement="top">
                        <!-- <span v-if="showOriginalValues ? originalQuestionnaireContacts[index]?.isDeleted : contact.isDeleted" class="tag is-danger">Removed</span>
                        <span v-else class="tag is-success">Active</span> -->
                        <input type="checkbox" :checked="showOriginalValues ? originalQuestionnaireContacts[index]?.isDeleted : contact.isDeleted" disabled />
                        <template #content>
                          <input type="checkbox" :checked="originalQuestionnaireContacts[index]?.isDeleted" disabled />
                        </template>
                      </Popper>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <div class="show-original-values" v-if="questionnaireData?.completedAt !== null && questionnaireData?.status >= 2">
          <b-switch type="is-info" v-model="showOriginalValues" />
          <small> Show Original Values </small>
        </div>

        <div class="action-buttons">
          <button type="button" class="button has-bcc-background has-text-white" @click="closeDialog('edit')"><i class="fas fa-edit"></i>&nbsp;Edit</button>
          <button type="button" class="button" @click="closeDialog(null)">Close</button>
        </div>
      </div>
    </div>
  </Box>
</template>

<script setup>
import Box from './Box.vue'
import { closeDialog } from 'vue3-promise-dialog'
import moment from 'moment'
import { computed, onBeforeMount, ref } from 'vue'
import Constants from '../../constants'

const { terminationReasons } = Constants

const props = defineProps({
  rawData: {
    type: Object,
    required: true,
  },
})

// Active tab state
const activeTab = ref('bank')
const questionnaireData = ref({})
const showOriginalValues = ref(false)

const originalQuestionnaireParticipants = computed(() => {
  return questionnaireData.value?.snapshot?.participants || []
})

const questionnaireParticipants = computed(() => {
  return questionnaireData.value?.participants || []
})

const originalQuestionnaireContacts = computed(() => {
  return questionnaireData.value?.snapshot?.contacts || []
})

const showCompensationColumns = computed(() => {
  return questionnaireData.value?.showCompensationColumns
})

const questionnaireContacts = computed(() => {
  return questionnaireData.value?.contacts || []
})

const originalBankContactInfo = computed(() => {
  return (
    questionnaireData.value?.snapshot?.clientData || {
      primaryAddress: {},
      mailingAddress: {},
      mailingAddressIsSame: false,
      clientPhone: '',
      clientFax: '',
      clientTIN: '',
    }
  )
})

const bankContactInfo = computed(() => {
  return (
    questionnaireData.value?.clientData || {
      primaryAddress: {},
      mailingAddress: {},
      mailingAddressIsSame: false,
      clientPhone: '',
      clientFax: '',
      clientTIN: '',
    }
  )
})

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return moment(dateString).format('MM/DD/YYYY')
}

const handleReasonText = (reasonValue) => {
  const reason = terminationReasons.filter((r) => `${r.value}` === `${reasonValue}`)[0]
  return reason?.label || '-'
}

const handleAvailability = (participant) => {
  return !!participant.terminationDate || !!participant.terminationReason || ['terminated', 'retired'].includes(participant.title?.toLowerCase()) ? true : false
}

const formatCurrency = (amount) => {
  if (!amount) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

onBeforeMount(() => {
  questionnaireData.value = props.rawData
})
</script>

<style scoped lang="scss">
@import './dialog.scss';

.dialog {
  min-width: 850px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tabs {
  margin-bottom: 0;

  ul {
    border-bottom: 1px solid #dbdbdb;
    margin-bottom: 1rem;
  }

  li {
    &.is-active {
      a {
        border-bottom-color: var(--bcc-color-default);
        color: var(--bcc-color-default);
        font-weight: 600;
      }
    }

    a {
      color: #4a4a4a;
      border-bottom: 2px solid transparent;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;

      &:hover {
        border-bottom-color: var(--bcc-color-default);
        color: var(--bcc-color-default);
      }
    }
  }
}

.participants-grid-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.table {
  margin-bottom: 0;
  font-size: 12px;

  .unavailable {
    opacity: 0.2 !important;
  }

  th {
    background-color: #f5f5f5;
    color: #363636;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid #dbdbdb;
  }

  td {
    vertical-align: middle;
    padding: 0.75em;

    &.participant-name,
    &.contact-name {
      min-width: 150px;
    }

    &.participant-title,
    &.participant-new-title,
    &.contact-title {
      min-width: 200px;
      max-width: 300px;
      word-wrap: break-word;
      font-size: 10px;
    }

    &.termination-date,
    &.termination-reason,
    &.salary,
    &.bonus,
    &.other-compensation {
      min-width: 120px;
      text-align: right;
    }

    &.contact-phone,
    &.contact-email {
      min-width: 150px;
    }

    &.contact-status {
      min-width: 100px;
      text-align: center;
    }
  }

  tbody tr:hover {
    background-color: #f5f5f5;
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #999;
  font-style: italic;
}

.actions {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  padding: 15px;
  margin-top: 15px;

  .show-original-values {
    display: flex;
    align-items: center;
    margin-right: auto;

    small {
      margin-left: 8px;
      font-size: 0.875rem;
      color: #4a4a4a;
    }
  }

  .action-buttons {
    display: flex;
    gap: 10px;
  }
}

.tag {
  &.is-success {
    background-color: #48c774;
    color: white;
  }

  &.is-danger {
    background-color: #f14668;
    color: white;
  }

  &.is-warning {
    background-color: #ffdd57;
    color: rgba(0, 0, 0, 0.7);
  }
}

.bank-info-container {
  padding: 1rem;
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  .info-section {
    padding: 1rem;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    height: 100%;

    .section-title {
      margin-bottom: 1rem;
      color: var(--bcc-color-default, #3273dc);
      border-bottom: 1px solid #e8e8e8;
      padding-bottom: 0.5rem;
    }

    .info-field {
      margin-bottom: 0.75rem;

      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #4a4a4a;
        margin-bottom: 0.25rem;
      }

      .field-value {
        font-size: 0.875rem;
        color: #363636;
        padding: 0.375rem 0;
        border-bottom: 1px solid #f0f0f0;
        min-height: 1.5rem;
        display: flex;
        align-items: center;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
</style>
